import clsx from "clsx";
import PropTypes from "prop-types";
import { memo } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";
import IconLink from "../../common/FormControl/IconLink";

const useStyles = makeStyles(theme => ({
  actionContainer: {
    margin: "auto 0",
    padding: "6px",
    paddingRight: 0,
    backgroundColor: "#ffffff40",
    borderRight: "none",
    borderRadius: "4px 0 0 4px",
  },
  iconBackground: {
    backgroundColor: "white",
    border: "5px solid white",
    borderRadius: "50%",
  },
  iconUnlock: {
    padding: 0,
    minWidth: "unset",
    color: theme.palette.secondary.main,
  },
  iconLock: {
    padding: 0,
    minWidth: "unset",
    color: theme.palette.secondary.dark,
    fontSize: 12,
  },
}));

const ProductTableItemActionsCell = ({
  canDownloadFilesAndFolders,
  canLock,
  hovered,
  id,
  itemCanBeUpdated,
  locked,
  lockedByAnotherUser,
  lockedByFolder,
  lockedByUsername,
  name,
  onDelete,
  onDownload,
  onEdit,
  onToggleLock,
}) => {
  const classes = useStyles();

  const lockClasses = [
    locked ? classes.iconLock : classes.iconUnlock,
    classes.iconBackground,
  ];

  return (
    <Box className={classes.actionContainer}>
      {hovered && (
        <>
          {canDownloadFilesAndFolders && (
            <IconLink
              icon="fa fa-download"
              placement="top"
              title={<FormattedMessage id="btn.download" />}
              onClick={onDownload}
              customClass={classes.iconBackground}
            />
          )}

          {itemCanBeUpdated && (
            <IconLink
              icon="fa fa-edit"
              placement="top"
              title={<FormattedMessage id="product.edit" />}
              onClick={onEdit}
              customClass={classes.iconBackground}
            />
          )}

          {canLock && (
            <IconLink
              icon={`fas ${locked ? "fa-lock" : "fa-unlock"}`}
              customClass={lockClasses}
              placement="top"
              title={<FormattedMessage id="product.lockUnlock" />}
              onClick={() =>
                onToggleLock(
                  id,
                  name,
                  locked,
                  lockedByUsername,
                  lockedByAnotherUser
                )
              }
              disabled={lockedByFolder}
            />
          )}

          {itemCanBeUpdated && (
            <IconLink
              icon="fas fa-trash"
              placement="top"
              title={<FormattedMessage id="context.delete" />}
              onClick={() => onDelete(id)}
              customClass={classes.iconBackground}
            />
          )}
        </>
      )}

      {locked && !hovered && (
        <Icon className={clsx("fas fa-lock", classes.iconLock)} />
      )}
    </Box>
  );
};

ProductTableItemActionsCell.propTypes = {
  canDownloadFilesAndFolders: PropTypes.bool.isRequired,
  canLock: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  itemCanBeUpdated: PropTypes.bool.isRequired,
  locked: PropTypes.bool.isRequired,
  lockedByAnotherUser: PropTypes.bool.isRequired,
  lockedByFolder: PropTypes.bool,
  lockedByUsername: PropTypes.string,
  name: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleLock: PropTypes.func.isRequired,
};

ProductTableItemActionsCell.defaultProps = {
  lockedByFolder: false,
  lockedByUsername: "",
  name: "",
};

export default memo(ProductTableItemActionsCell);
